import { useLocation } from "react-router-dom";
import { useSeason } from "../../../context/SeasonContext";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAdminData, useAdminTask } from "../useAdminData";
import { formPayload, initialForm, number } from "../adminModel";
import styles from "../admin.module.css";

const ICON_PATHS = {
  overview: "M3 3h7v7H3z M14 3h7v7h-7z M3 14h7v7H3z M14 14h7v7h-7z",
  users:
    "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M16 3a4 4 0 0 1 0 8 M22 21v-2a4 4 0 0 0-3-3.87 M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0",
  teams: "M3 21h18 M5 21V7l7-4 7 4v14 M9 21v-6h6v6 M9 9h.01 M15 9h.01",
  globe:
    "M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0 M3 12h18 M12 3a17 17 0 0 1 0 18 17 17 0 0 1 0-18",
  cases: "M8 6V4h8v2 M3 7h18v14H3z M3 12a22 22 0 0 0 18 0 M10 12v3h4v-3",
  calendar:
    "M5 4h14a2 2 0 0 1 2 2v14H3V6a2 2 0 0 1 2-2 M7 2v4 M17 2v4 M3 10h18 M7 14h2 M13 14h2",
  award: "M16 8a4 4 0 1 1-8 0 4 4 0 0 1 8 0 M9 12l-2 9 5-3 5 3-2-9",
  chart: "M4 3v18h17 M8 16v-4 M13 16V8 M18 16V5",
  upload: "M12 16V3 M7 8l5-5 5 5 M3 15v6h18v-6",
  download: "M12 3v13 M7 11l5 5 5-5 M3 17v4h18v-4",
  search: "M16 10a6 6 0 1 1-12 0 6 6 0 0 1 12 0 M15 15l6 6",
  plus: "M12 5v14 M5 12h14",
  close: "M6 6l12 12 M6 18L18 6",
  arrow: "M5 12h14 M14 7l5 5-5 5",
  external: "M14 3h7v7 M21 3L10 14 M10 3H3v18h18v-7",
  refresh: "M20 7a9 9 0 1 0 1 8 M20 3v5h-5",
  check: "M5 12l4 4L19 6",
  alert: "M12 9v4 M12 17h.01 M12 3L2 21h20z",
  edit: "M16 3l5 5-12 12H4v-5z M13 6l5 5",
  more: "M5 12h.01 M12 12h.01 M19 12h.01",
  wallet: "M3 6h18v15H3V6z M3 6V3h15v3 M15 11h6v5h-6z",
  file: "M14 2H4v20h16V8z M14 2v6h6 M8 13h8 M8 17h5",
  shield: "M12 3l8 3v6c0 5-8 9-8 9s-8-4-8-9V6z M8 12l3 3 5-6",
};
export function Icon({ name, size = 18, ...props }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={name === "more" ? 3 : 1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d={ICON_PATHS[name] || ICON_PATHS.file} />
    </svg>
  );
}
function useContentReadOnly() {
  const { season, canEdit } = useSeason();
  const location = useLocation();
  const section = new URLSearchParams(location.search).get('section');
  return Boolean(season && !canEdit && !['seasons','users'].includes(section));
}
export function Button({
  children,
  variant = "secondary",
  icon,
  className = "",
  type = "button",
  ...props
}) {
  const readOnly = useContentReadOnly();
  const mutation = type === 'submit' || ['plus', 'edit'].includes(icon);
  return (
    <button
      type={type}
      className={`${styles.button} ${styles[`button_${variant}`]} ${className}`}
      {...props}
      disabled={props.disabled || (readOnly && mutation)}
      title={readOnly && mutation ? 'Сезон доступен только для просмотра' : props.title}
    >
      {icon && <Icon name={icon} />}
      {children}
    </button>
  );
}
export function Badge({ children, tone = "neutral" }) {
  return (
    <span className={`${styles.badge} ${styles[`badge_${tone}`]}`}>
      {children}
    </span>
  );
}
export function Notice({ children, danger = false, onClose }) {
  if (!children) return null;
  return (
    <div
      className={`${styles.notice} ${danger ? styles.noticeError : ""}`}
      role={danger ? "alert" : "status"}
    >
      <Icon name={danger ? "alert" : "check"} />
      <div>{children}</div>
      {onClose && (
        <button
          type="button"
          className={styles.iconButton}
          aria-label="Закрыть уведомление"
          onClick={onClose}
        >
          <Icon name="close" size={16} />
        </button>
      )}
    </div>
  );
}
export function Field({ label, hint, wide, suggestions, children, ...props }) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const shared = {
    id,
    "aria-describedby": hintId,
    list: suggestions?.length ? `${id}-suggestions` : undefined,
    ...props,
  };
  return (
    <div className={`${styles.field} ${wide ? styles.fieldWide : ""}`}>
      <label htmlFor={id}>
        {label}
        {props.required && <span className={styles.required}> *</span>}
      </label>
      {children ? (
        <select className={styles.input} {...shared}>
          {children}
        </select>
      ) : props.type === "textarea" ? (
        <textarea className={styles.textarea} {...shared} type={undefined} />
      ) : (
        <input className={styles.input} {...shared} />
      )}
      {suggestions?.length > 0 && (
        <datalist id={`${id}-suggestions`}>
          {suggestions.map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>
      )}
      {hint && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
    </div>
  );
}
export function CheckField({ label, hint, ...props }) {
  return (
    <label className={styles.checkField}>
      <input type="checkbox" {...props} />
      <span>
        <strong>{label}</strong>
        {hint && <span className={styles.hint}>{hint}</span>}
      </span>
    </label>
  );
}
export function SearchField({
  value,
  onChange,
  placeholder = "Найти…",
  label = "Поиск",
}) {
  return (
    <div className={styles.searchField}>
      <Icon name="search" />
      <input
        type="search"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {value && (
        <button
          type="button"
          aria-label="Очистить поиск"
          onClick={() => onChange("")}
        >
          <Icon name="close" size={15} />
        </button>
      )}
    </div>
  );
}
export function FilterSelect({ label, value, onChange, options }) {
  return (
    <select
      className={styles.filterSelect}
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
export function SectionHeader({ title, description, children }) {
  return (
    <header className={styles.sectionHeader}>
      <div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      <div className={styles.headerActions}>{children}</div>
    </header>
  );
}
export function Toolbar({ children, onRefresh, loading, onReset, filtered }) {
  return (
    <div className={styles.toolbar}>
      {children}
      <div className={styles.toolbarEnd}>
        {filtered && (
          <button type="button" className={styles.textButton} onClick={onReset}>
            Сбросить
          </button>
        )}
        <Button
          icon="refresh"
          aria-label="Обновить список"
          title="Обновить список"
          onClick={onRefresh}
          disabled={loading}
        />
      </div>
    </div>
  );
}
export function EmptyState({
  title = "Ничего не найдено",
  description = "Попробуйте изменить запрос или сбросить фильтры.",
  action,
}) {
  return (
    <div className={styles.emptyState}>
      <span className={styles.emptyIcon}>
        <Icon name="search" size={24} />
      </span>
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}
export function ResourceContent({
  resource,
  children,
  emptyTitle,
  emptyDescription,
  emptyAction,
}) {
  if (resource.loading)
    return (
      <div
        className={styles.loadingState}
        role="status"
        aria-label="Загрузка данных"
      >
        <span className={styles.srOnly}>Загружаем данные…</span>
        {[0, 1, 2, 3, 4].map((index) => (
          <div className={styles.skeletonRow} key={index} />
        ))}
      </div>
    );
  if (resource.error)
    return (
      <div className={styles.resourceError}>
        <Notice danger>{resource.error}</Notice>
        <Button onClick={resource.refresh} icon="refresh">
          Повторить загрузку
        </Button>
      </div>
    );
  if (!resource.items.length)
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
      />
    );
  return children;
}
export function DataTable({ columns, items, rowKey, caption }) {
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <caption className={styles.srOnly}>{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th scope="col" key={column.key} className={column.className}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item[rowKey]}>
              {columns.map((column) => (
                <td
                  data-label={column.label}
                  className={column.className}
                  key={column.key}
                >
                  {column.render(item)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
export function Pagination({ query, resource }) {
  const { limit, offset } = query.values;
  const total = resource.total;
  const lastOffset = Math.max(0, (Math.ceil(total / limit) - 1) * limit);
  useEffect(() => {
    if (!resource.loading && !resource.error && offset > lastOffset)
      query.update({ offset: lastOffset });
  });
  return (
    <div className={styles.pagination}>
      <span className={styles.resultCount} aria-live="polite">
        {resource.loading
          ? "Загрузка…"
          : total
            ? `${number(offset + 1)}–${number(Math.min(offset + limit, total))} из ${number(total)}`
            : "0 записей"}
      </span>
      <div className={styles.paginationControls}>
        <label>
          На странице{" "}
          <select
            aria-label="Записей на странице"
            value={limit}
            onChange={(event) => query.update({ limit: event.target.value })}
          >
            {[25, 50, 100].map((size) => (
              <option key={size}>{size}</option>
            ))}
          </select>
        </label>
        <Button
          disabled={resource.loading || offset === 0}
          onClick={() => query.update({ offset: Math.max(0, offset - limit) })}
          aria-label="Предыдущая страница"
        >
          ←
        </Button>
        <span>
          {Math.floor(offset / limit) + 1} /{" "}
          {Math.max(1, Math.ceil(total / limit))}
        </span>
        <Button
          disabled={resource.loading || offset + limit >= total}
          onClick={() => query.update({ offset: offset + limit })}
          aria-label="Следующая страница"
        >
          →
        </Button>
      </div>
    </div>
  );
}
export function ActionMenu({ label, actions }) {
  const readOnly = useContentReadOnly();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({});
  const trigger = useRef(null);
  const menu = useRef(null);
  const id = useId();
  useEffect(() => {
    if (!open) return undefined;
    menu.current?.querySelector("button:not(:disabled)")?.focus();
    const close = (event) => {
      if (
        !menu.current?.contains(event.target) &&
        !trigger.current?.contains(event.target)
      )
        setOpen(false);
    };
    const onScroll = (event) => {
      if (!menu.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("pointerdown", close);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);
  const closeMenu = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={styles.iconButton}
        aria-label={`Действия: ${label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => {
          const rect = trigger.current.getBoundingClientRect();
          setPosition({
            right: Math.max(12, window.innerWidth - rect.right),
            ...(rect.bottom + actions.length * 42 + 20 > window.innerHeight
              ? { bottom: window.innerHeight - rect.top + 4 }
              : { top: rect.bottom + 4 }),
          });
          setOpen(!open);
        }}
      >
        <Icon name="more" />
      </button>
      {open &&
        createPortal(
          <div
            id={id}
            ref={menu}
            role="menu"
            aria-label={label}
            className={styles.actionMenu}
            style={position}
            onKeyDown={(event) => {
              if (event.key === "Escape" || event.key === "Tab") {
                closeMenu();
                return;
              }
              const options = [
                ...menu.current.querySelectorAll("button:not(:disabled)"),
              ];
              const index = options.indexOf(document.activeElement);
              if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
                event.preventDefault();
                options[
                  event.key === "Home"
                    ? 0
                    : event.key === "End"
                      ? options.length - 1
                      : (index +
                          (event.key === "ArrowDown" ? 1 : -1) +
                          options.length) %
                        options.length
                ]?.focus();
              }
            }}
          >
            {actions.filter(Boolean).map((action) => (
              <button
                type="button"
                role="menuitem"
                key={action.label}
                className={action.danger ? styles.dangerText : ""}
                disabled={action.disabled || (readOnly && !action.readOnly)}
                onClick={() => {
                  closeMenu();
                  action.onClick();
                }}
              >
                {action.icon && <Icon name={action.icon} size={16} />}
                {action.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
export function Dialog({
  title,
  description,
  children,
  footer,
  onClose,
  busy = false,
  dirty = false,
  compact = false,
}) {
  const ref = useRef(null);
  const id = useId();
  const [discard, setDiscard] = useState(false);
  useEffect(() => {
    const node = ref.current;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    node.showModal();
    return () => {
      node.close();
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, []);
  useEffect(() => {
    if (!dirty && !busy) return undefined;
    const preventUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [dirty, busy]);
  const requestClose = () => {
    if (busy) return;
    if (dirty) setDiscard(true);
    else onClose();
  };
  return createPortal(
    <dialog
      ref={ref}
      aria-labelledby={`${id}-title`}
      aria-describedby={description ? `${id}-description` : undefined}
      className={`${styles.dialog} ${compact ? styles.dialogCompact : styles.dialogDrawer}`}
      onCancel={(event) => {
        event.preventDefault();
        requestClose();
      }}
    >
      <div className={styles.dialogHeader}>
        <div>
          <h2 id={`${id}-title`}>{title}</h2>
          {description && <p id={`${id}-description`}>{description}</p>}
        </div>
        <button
          type="button"
          className={styles.iconButton}
          aria-label="Закрыть панель"
          onClick={requestClose}
          disabled={busy}
        >
          <Icon name="close" />
        </button>
      </div>
      <div className={styles.dialogBody}>{children}</div>
      <div className={styles.dialogFooter}>
        {discard ? (
          <div className={styles.discardPrompt} role="alert">
            <strong>Закрыть без сохранения?</strong>
            <p>Внесённые изменения будут потеряны.</p>
            <div className={styles.inlineActions}>
              <Button onClick={() => setDiscard(false)}>
                Продолжить редактирование
              </Button>
              <Button variant="danger" onClick={onClose}>
                Не сохранять
              </Button>
            </div>
          </div>
        ) : typeof footer === "function" ? (
          footer(requestClose)
        ) : (
          footer
        )}
      </div>
    </dialog>,
    document.body,
  );
}
export function ConfirmDialog({
  title,
  description,
  confirmLabel = "Подтвердить",
  danger,
  onConfirm,
  onClose,
  requiredText,
  children,
}) {
  const task = useAdminTask();
  const [text, setText] = useState("");
  return (
    <Dialog
      compact
      title={title}
      description={description}
      onClose={onClose}
      busy={task.busy}
      footer={
        <>
          <Notice danger>{task.error}</Notice>
          <div className={styles.footerButtons}>
            <Button onClick={onClose} disabled={task.busy}>
              Отмена
            </Button>
            <Button
              variant={danger ? "danger" : "primary"}
              disabled={task.busy || (requiredText && text !== requiredText)}
              onClick={() => task.run(onConfirm, onClose)}
            >
              {task.busy ? "Выполняем…" : confirmLabel}
            </Button>
          </div>
        </>
      }
    >
      <div
        className={`${styles.confirmIcon} ${danger ? styles.dangerText : ""}`}
      >
        <Icon name={danger ? "alert" : "shield"} size={28} />
      </div>
      {children}
      {requiredText && (
        <Field
          label={`Введите «${requiredText}» для подтверждения`}
          value={text}
          onChange={(event) => setText(event.target.value)}
          autoComplete="off"
        />
      )}
    </Dialog>
  );
}
export function EntityEditor({
  title,
  description,
  fields,
  item,
  onSave,
  onClose,
  onSaved,
  children,
}) {
  const [form, setForm] = useState(() => initialForm(fields, item));
  const original = useRef(JSON.stringify(form));
  const task = useAdminTask();
  const id = useId();
  const groups = [];
  fields.forEach((field) => {
    if (field.group || !groups.length)
      groups.push({ name: field.group || "", fields: [] });
    groups[groups.length - 1].fields.push(field);
  });
  return (
    <Dialog
      title={title}
      description={description}
      onClose={onClose}
      busy={task.busy}
      dirty={JSON.stringify(form) !== original.current}
      footer={(requestClose) => (
        <>
          <Notice danger>{task.error}</Notice>
          <div className={styles.footerButtons}>
            <span className={styles.hint}>* Обязательные поля</span>
            <Button onClick={requestClose} disabled={task.busy}>
              Отмена
            </Button>
            <Button
              variant="primary"
              type="submit"
              form={id}
              disabled={task.busy}
            >
              {task.busy ? "Сохраняем…" : "Сохранить"}
            </Button>
          </div>
        </>
      )}
    >
      <form
        id={id}
        onSubmit={(event) => {
          event.preventDefault();
          const payload = formPayload(fields, form);
          const invalid = fields.find(
            (field) =>
              field.required &&
              (!String(payload[field.name] || "").trim() ||
                String(payload[field.name]).trim().length <
                  (field.minLength || 1)),
          );
          if (invalid) {
            task.setError(`Проверьте поле «${invalid.label}».`);
            return;
          }
          task.run(
            () => onSave(payload),
            (result) => {
              onSaved?.(result);
              onClose();
            },
          );
        }}
      >
        {children}
        {groups.map((group, index) => (
          <fieldset key={index} className={styles.formGroup}>
            {group.name && <legend>{group.name}</legend>}
            <div className={styles.formGrid}>
              {group.fields.map((field) =>
                field.type === "checkbox" ? (
                  <div className={styles.fieldWide} key={field.name}>
                    <CheckField
                      label={field.label}
                      hint={field.hint}
                      checked={Boolean(form[field.name])}
                      disabled={task.busy}
                      onChange={(event) =>
                        setForm((previous) => ({
                          ...previous,
                          [field.name]: event.target.checked,
                        }))
                      }
                    />
                  </div>
                ) : (
                  <Field
                    key={field.name}
                    label={field.label}
                    hint={field.hint}
                    wide={field.wide}
                    suggestions={field.suggestions}
                    required={field.required}
                    type={
                      field.type === "select" ? undefined : field.type || "text"
                    }
                    minLength={field.minLength}
                    maxLength={field.maxLength}
                    min={field.min}
                    placeholder={field.placeholder}
                    value={form[field.name]}
                    disabled={task.busy}
                    onChange={(event) =>
                      setForm((previous) => ({
                        ...previous,
                        [field.name]: event.target.value,
                      }))
                    }
                  >
                    {field.options
                      ? [
                          ...field.options,
                          ...(form[field.name] &&
                          !field.options.some(
                            (option) => option.value === form[field.name],
                          )
                            ? [
                                {
                                  value: form[field.name],
                                  label: form[field.name],
                                },
                              ]
                            : []),
                        ].map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))
                      : undefined}
                  </Field>
                ),
              )}
            </div>
          </fieldset>
        ))}
      </form>
    </Dialog>
  );
}
export function AsyncPicker({
  label,
  loader,
  value,
  onChange,
  getLabel,
  getMeta,
  idKey,
  required = false,
  disabled = false,
}) {
  const inputRef = useRef(null);
  const changeRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const resource = useAdminData(loader, { query, limit: 10 });
  const id = useId();
  const select = (option) => {
    onChange(option);
    setOpen(false);
    window.requestAnimationFrame(() => changeRef.current?.focus());
  };
  return (
    <div className={styles.picker}>
      <label htmlFor={value ? undefined : id} id={`${id}-label`}>
        {label}
        {required && <span className={styles.required}> *</span>}
      </label>
      {value ? (
        <div
          className={styles.pickerSelection}
          role="group"
          aria-labelledby={`${id}-label`}
        >
          <div>
            <strong>{getLabel(value)}</strong>
            <span>{getMeta?.(value)}</span>
          </div>
          <button
            ref={changeRef}
            type="button"
            className={styles.textButton}
            disabled={disabled}
            onClick={() => {
              onChange(null);
              setQuery("");
              setOpen(true);
              window.requestAnimationFrame(() => inputRef.current?.focus());
            }}
          >
            Изменить
          </button>
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            id={id}
            aria-required={required}
            role="combobox"
            aria-expanded={open}
            aria-controls={`${id}-options`}
            aria-autocomplete="list"
            aria-activedescendant={
              open && !resource.loading && resource.items[activeIndex]
                ? `${id}-option-${activeIndex}`
                : undefined
            }
            className={styles.input}
            placeholder="Начните вводить имя или название"
            value={query}
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onBlur={(event) => {
              if (
                !event.currentTarget.parentElement.contains(event.relatedTarget)
              )
                setOpen(false);
            }}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(0);
              setOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape" && open) {
                event.preventDefault();
                event.stopPropagation();
                setOpen(false);
              }
              if (["ArrowDown", "ArrowUp"].includes(event.key)) {
                event.preventDefault();
                setOpen(true);
                setActiveIndex((index) =>
                  Math.max(
                    0,
                    Math.min(
                      resource.items.length - 1,
                      index + (event.key === "ArrowDown" ? 1 : -1),
                    ),
                  ),
                );
              }
              if (event.key === "Enter" && open) {
                event.preventDefault();
                if (!resource.loading && resource.items[activeIndex]) {
                  select(resource.items[activeIndex]);
                }
              }
            }}
          />
          {open && (
            <div
              id={`${id}-options`}
              role="listbox"
              aria-label={label}
              className={styles.pickerOptions}
            >
              {resource.loading ? (
                <p>Поиск…</p>
              ) : resource.error ? (
                <p role="alert">{resource.error}</p>
              ) : resource.items.length ? (
                resource.items.map((option, index) => (
                  <button
                    id={`${id}-option-${index}`}
                    type="button"
                    role="option"
                    aria-selected={index === activeIndex}
                    key={option[idKey]}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      select(option);
                    }}
                  >
                    <strong>{getLabel(option)}</strong>
                    <span>{getMeta?.(option)}</span>
                  </button>
                ))
              ) : (
                <p>Совпадений не найдено. Измените запрос.</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
